import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, ArrowRight, Search, Phone, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import MinnesotaMap from "@/components/MinnesotaMap";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ServiceAreas() {
  useSEO();
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await axios.get(`${API}/cities`);
        setCities(response.data);
        setFilteredCities(response.data);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          if (process.env.NODE_ENV === 'development') {
            console.error('Failed to fetch cities:', err);
          }
        }
        // Cities list will be empty on error
      } finally {
        setLoading(false);
      }
    };
    fetchCities();
  }, []);

  useEffect(() => {
    let filtered = cities;

    if (searchQuery) {
      filtered = filtered.filter((city) =>
        city.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedRegion !== "all") {
      filtered = filtered.filter((city) => city.region === selectedRegion);
    }

    setFilteredCities(filtered);
  }, [searchQuery, selectedRegion, cities]);

  const regions = [...new Set(cities.map((c) => c.region))];

  const groupedCities = filteredCities.reduce((acc, city) => {
    if (!acc[city.region]) acc[city.region] = [];
    acc[city.region].push(city);
    return acc;
  }, {});

  return (
    <div data-testid="service-areas-page" className="">
      {/* Hero Section */}
      <section className="bg-navy py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-orange/20 text-orange text-sm font-medium px-4 py-2 rounded-full mb-6">
              <MapPin className="w-4 h-4" />
              Statewide Coverage
            </div>
            <h1
              data-testid="service-areas-title"
              className="font-heading text-4xl sm:text-5xl font-semibold text-white tracking-tight mb-6"
            >
              We Serve All of Minnesota
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              From the Twin Cities metro to greater Minnesota, we help homeowners 
              across the entire state. Browse our featured service areas below.
            </p>
          </div>
        </div>
      </section>

      {/* Map & Search Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left: Map */}
            <div>
              <h2 className="font-heading text-2xl font-semibold text-navy mb-4">
                Interactive Service Map
              </h2>
              <p className="text-gray-600 mb-6">
                Click on any region to explore cities in that area. Numbers show how many cities we serve in each region.
              </p>
              <MinnesotaMap cities={cities} />
            </div>

            {/* Right: Search & Stats */}
            <div>
              {/* Search */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-6">
                <h3 className="font-heading text-lg font-semibold text-navy mb-4">
                  Find Your City
                </h3>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      data-testid="city-search-input"
                      placeholder="Search cities..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-12"
                    />
                  </div>
                  <select
                    data-testid="region-filter"
                    value={selectedRegion}
                    onChange={(e) => setSelectedRegion(e.target.value)}
                    className="h-12 px-4 border border-gray-200 rounded-lg bg-white text-navy"
                  >
                    <option value="all">All Regions</option>
                    {regions.map((region) => (
                      <option key={region} value={region}>
                        {region}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-orange/5 border border-orange/20 rounded-xl p-4 text-center">
                  <div className="text-3xl font-heading font-bold text-orange">
                    {cities.length}
                  </div>
                  <div className="text-gray-600 text-sm">Cities</div>
                </div>
                <div className="bg-navy/5 border border-navy/20 rounded-xl p-4 text-center">
                  <div className="text-3xl font-heading font-bold text-navy">
                    {regions.length}
                  </div>
                  <div className="text-gray-600 text-sm">Regions</div>
                </div>
                <div className="bg-navy/5 border border-navy/20 rounded-xl p-4 text-center">
                  <div className="text-3xl font-heading font-bold text-navy">24hr</div>
                  <div className="text-gray-600 text-sm">Response</div>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-navy rounded-xl p-6">
                <h3 className="font-heading text-xl font-semibold text-white mb-2">
                  Don't See Your City?
                </h3>
                <p className="text-gray-400 mb-4">
                  We serve ALL of Minnesota. Get in touch today.
                </p>
                <Link to="/#get-started">
                  <Button className="w-full bg-orange hover:bg-orange-hover text-white">
                    <Phone className="w-4 h-4 mr-2" />
                    Get Connected
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* City List */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-semibold text-navy mb-4">
              Featured Service Areas
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Browse storm damage resources for these Minnesota communities. 
              We serve the entire state, not just these featured areas.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading cities...</div>
          ) : (
            <div className="space-y-10">
              {Object.entries(groupedCities).map(([region, regionCities]) => (
                <div key={region} className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="font-heading text-xl font-semibold text-navy mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-orange" />
                    {region}
                    <span className="text-gray-400 font-normal text-sm ml-2">
                      {regionCities.length} {regionCities.length === 1 ? 'city' : 'cities'}
                    </span>
                  </h3>
                  <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {regionCities.map((city) => (
                      <Link
                        key={city.slug}
                        to={`/service-areas/${city.slug}`}
                        data-testid={`city-link-${city.slug}`}
                        className="group flex items-center gap-2 p-3 rounded-lg hover:bg-orange/5 transition-colors"
                      >
                        <CheckCircle className="w-4 h-4 text-orange flex-shrink-0" />
                        <span className="font-medium text-navy group-hover:text-orange transition-colors">
                          {city.name}
                        </span>
                        <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-orange group-hover:translate-x-1 transition-all ml-auto" />
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
